import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import aiDevelopmentService from "../../services/aiDevelopmentService";
import { AI_WORKFLOW_STAGES, workflowStage, isWorkflowProblem } from "./AiWorkflowProgress";

const KANBAN_PAGE_SIZE = 100;
// Safety cap on how many pages a single board load will fetch, so a runaway backend response
// (or an unexpectedly huge project) can't turn this into an unbounded fetch loop.
const MAX_PAGES = 20;

/**
 * Taiga-style Kanban board: one ordered column per workflow stage (the same analysis/plan/
 * development/qa/review/completed grouping already used by AiWorkflowProgress, not one column
 * per raw AiWorkflowStatus - 17 raw statuses would make an unusably wide board). Read-only: cards
 * link to the story detail page, there is no drag-and-drop status change here.
 */
export default function AiDevelopmentKanbanBoard({ projectId, t }) {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const all = [];
      let page = 0;
      let totalPages = 1;
      while (page < totalPages && page < MAX_PAGES) {
        const result = await aiDevelopmentService.stories("", page, KANBAN_PAGE_SIZE, projectId);
        all.push(...(result?.content || []));
        totalPages = result?.totalPages || 1;
        page += 1;
      }
      setStories(all);
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setLoading(false);
    }
  }, [projectId, t]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <p>{t("common.loading")}</p>;

  const columns = AI_WORKFLOW_STAGES.map((step, index) => ({
    step,
    stories: stories.filter((s) => workflowStage(s.status) === index),
  }));

  return (
    <div>
      {error && <div className="ai-studio-alert">{error}</div>}
      <div className="ai-kanban" aria-label={t("aiDevelopment.kanbanBoard")}>
        {columns.map(({ step, stories: columnStories }) => (
          <div className="ai-kanban-column" key={step}>
            <div className="ai-kanban-column-header">
              <b>{t(`aiDevelopment.workflow.${step}`)}</b>
              <span className="ai-kanban-count">{columnStories.length}</span>
            </div>
            <div className="ai-kanban-column-body">
              {columnStories.map((story) => (
                <Link
                  key={story.id}
                  to={`/ai-development/stories/${story.id}`}
                  className={`ai-kanban-card${isWorkflowProblem(story.status) ? " is-problem" : ""}`}
                >
                  <span className="ai-kanban-card-title">{story.title}</span>
                  <span className="ai-kanban-card-meta">
                    {story.externalReference ? `${story.externalReference} · ` : ""}
                    {story.projectName || story.projectId}
                  </span>
                  <span className="ai-status">{story.priority}</span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
